import ReportIssueBar from '@/components/ReportIssueBar';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Upload, FileText, GitCompare, AlertCircle, X } from "lucide-react";
import axios from "axios";
import toast from 'react-hot-toast';
import MatrixBackground from "@/MatrixBackground";
import AnimatedToolTitle from '@/components/AnimatedToolTitle';
import AnimatedActionButton from '@/components/AnimatedActionButton';
import LedProgressLine from "@/components/LedProgressLine";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const getToken = () => {
  const token = localStorage.getItem('token');
  if (token) return token;
  const trialUser = localStorage.getItem('trial_user');
  if (trialUser) return 'trial_user_token';
  return null;
};

const DocumentComparison = ({ onBack }) => {
    // Set page title
  useEffect(() => {
    document.title = "Document Comparison - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);

  const navigate = useNavigate();
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [comparisonResults, setComparisonResults] = useState(null);

  const handleFile1Change = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error("Error");
        return;
      }
      setFile1(selectedFile);
      setComparisonResults(null);
    }
  };

  const handleFile2Change = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error("Error");
        return;
      }
      setFile2(selectedFile);
      setComparisonResults(null);
    }
  };

  const handleCompare = async () => {
    if (!file1 || !file2) {
      toast.error("Error");
      return;
    }

    setComparing(true);
    const formData = new FormData();
    formData.append('file1', file1);
    formData.append('file2', file2);

    try {
      const response = await axios.post(
        `${API}/contracts/compare`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setComparisonResults(response.data);
      toast.success("Success");
    } catch (error) {
      console.error('Comparison error:', error);
      toast.error("Error");
    } finally {
      setComparing(false);
    }
  };

  const handleReset = () => {
    setFile1(null);
    setFile2(null);
    setComparisonResults(null);
  };

  const getImpactLevelColor = (level) => {
    const colors = {
      'High': 'text-red-500',
      'Medium': 'text-yellow-500',
      'Low': 'text-green-500'
    };
    return colors[level] || 'text-gray-500';
  };

  return (
    <div className="tool-page">
      <MatrixBackground />
      <div className="tool-header">
        <Button
          variant="ghost"
          onClick={() => onBack ? onBack() : navigate('/dashboard')}
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </Button>
        <div className="tool-title-section">
          <GitCompare size={40} className="tool-icon gold-icon" />
          <div>
            <AnimatedToolTitle title="Compare Documents" />
            <p className="tool-subtitle">Compare two contracts side-by-side to identify key differences and risks</p>
          </div>
        </div>
      </div>

      <div className="tool-content">
        {!comparisonResults ? (
          <>
            {/* Upload and Compare Card */}
            <Card className="generation-form-card">
              <CardHeader>
                <CardTitle>Document Comparison</CardTitle>
                <CardDescription>
                  Upload two contracts to compare key clauses, terms, and identify material differences
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="form-group">
                  <label className="upload-label">Contract 1 *</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="file1-input"
                      accept=".pdf,.docx"
                      onChange={handleFile1Change}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="file1-input" className="file-upload-label">
                      <div className="upload-icon-tool">
                        {file1 ? <FileText size={48} /> : <Upload size={48} />}
                      </div>
                      <p className="upload-text-tool">
                        {file1 ? file1.name : 'Click to upload first contract (PDF or DOCX)'}
                      </p>
                      <p className="upload-subtext-tool">Maximum file size: 10MB</p>
                    </label>
                  </div>
                </div>

                <div className="form-group" style={{ marginTop: '20px' }}>
                  <label className="upload-label">Contract 2 *</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="file2-input"
                      accept=".pdf,.docx"
                      onChange={handleFile2Change}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="file2-input" className="file-upload-label">
                      <div className="upload-icon-tool">
                        {file2 ? <FileText size={48} /> : <Upload size={48} />}
                      </div>
                      <p className="upload-text-tool">
                        {file2 ? file2.name : 'Click to upload second contract (PDF or DOCX)'}
                      </p>
                      <p className="upload-subtext-tool">Maximum file size: 10MB</p>
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <AnimatedActionButton
                onClick={handleCompare}
                disabled={comparing || !file1 || !file2}
                loading={comparing}
                size="lg"
              >
                Compare Documents
              </AnimatedActionButton>
                  <LedProgressLine isActive={comparing} />
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          /* Results View */
          <>
            <div className="results-header">
              <h2>Comparison Results</h2>
              <Button onClick={handleReset} variant="outline">
                New Comparison
              </Button>
            </div>

            {/* Summary Card */}
            <Card className="result-card">
              <CardHeader>
                <CardTitle>Executive Summary</CardTitle>
                <CardDescription>Overview of the comparison</CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="analysis-content"
                  dangerouslySetInnerHTML={{ __html: comparisonResults.summary }}
                />
              </CardContent>
            </Card>

            {/* Overall Risk Assessment */}
            <Card className="result-card">
              <CardHeader>
                <CardTitle>Overall Assessment</CardTitle>
                <CardDescription>Which contract is more favorable</CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="analysis-content"
                  dangerouslySetInnerHTML={{ __html: comparisonResults.overall_risk_assessment }}
                />
              </CardContent>
            </Card>

            {/* Major Differences */}
            {comparisonResults.major_differences && comparisonResults.major_differences.length > 0 && (
              <Card className="result-card">
                <CardHeader>
                  <CardTitle>
                    <AlertCircle className="inline mr-2" size={24} />
                    Major Differences
                  </CardTitle>
                  <CardDescription>Key changes to be aware of</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="differences-list">
                    {comparisonResults.major_differences.map((diff, index) => (
                      <li key={index} className="difference-item">
                        {diff}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Clause Differences */}
            {comparisonResults.clause_differences && comparisonResults.clause_differences.length > 0 && (
              <Card className="result-card">
                <CardHeader>
                  <CardTitle>Clause-by-Clause Comparison</CardTitle>
                  <CardDescription>Detailed comparison of key clauses</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="clauses-grid">
                    {comparisonResults.clause_differences.map((clause, index) => (
                      <div key={index} className="clause-item">
                        <div className="clause-header">
                          <GitCompare size={20} className="gold-icon" />
                          <h4>{clause.clause_type}</h4>
                          <span className={`impact-badge ${getImpactLevelColor(clause.impact_level)}`}>
                            {clause.impact_level} Impact
                          </span>
                        </div>
                        
                        <div className="clause-comparison">
                          <div className="clause-section">
                            <strong>Contract 1:</strong>
                            <div 
                              className="analysis-content"
                              dangerouslySetInnerHTML={{ __html: clause.document1_content }}
                            />
                          </div>
                          
                          <div className="clause-section">
                            <strong>Contract 2:</strong>
                            <div 
                              className="analysis-content"
                              dangerouslySetInnerHTML={{ __html: clause.document2_content }}
                            />
                          </div>
                        </div>

                        <div className="clause-analysis">
                          <strong>Analysis:</strong>
                          <div 
                            className="analysis-content"
                            dangerouslySetInnerHTML={{ __html: clause.analysis }}
                          />
                        </div>
                      </div>
                    ))}
        <ReportIssueBar toolName="Document Comparison" />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recommendations */}
            <Card className="result-card">
              <CardHeader>
                <CardTitle>Recommendations</CardTitle>
                <CardDescription>Suggested actions and considerations</CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="analysis-content"
                  dangerouslySetInnerHTML={{ __html: comparisonResults.recommendations }}
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentComparison;

